import styled from "@emotion/styled";
import { ReactNode, useEffect, useMemo, useState } from "react";
import throttle from "lodash/throttle";
import { HeaderMenuScrollContext, HeaderMenuScrollContextType } from "../header/HeaderMenuScrollContext";
import { HeaderColor } from "@kaltura/mediaspace-shared-types";
import { headerHeight } from "@kaltura/mediaspace-shared-constants";

export interface HeaderMenuContainerProps {
    sticky?: boolean;
    headerColor: HeaderColor; // Kms_Resource_Config::getConfiguration('header', 'headerStyle'); or transparent for hero pages
    scrollThreshold?: number;
    children?: ReactNode;
}

interface StyledHeaderProps {
    sticky?: boolean;
    scrollRatio?: number;
    headerColor?: string;
}

const StyledHeaderMenu = styled.div<StyledHeaderProps>(({ theme, sticky }) => ({
    // The header menu is always on top of the page, so we need to set a high z-index
    // playkit-top-bar has zindex of 2, thus blocking access to the header menu dropdown
    // when entry is playing and the player has plugins that enable playkit-top-bar like download or moderation
    zIndex: theme.zIndex.appBar,
    backgroundColor: theme.kaltura.palette.surfaces.background,
    ...(
        sticky && {
            top: 0,
            position: "sticky",
        }
    ),
}));

const StyledNavbar = styled.div<StyledHeaderProps>(({ theme }) => ({
    padding: theme.spacing(1, 3),
    display: "flex",
    alignItems: "center",
    height: headerHeight,
}));

/**
 * Site Header Menu component (with navigation, user menu, etc.)
 */
export const HeaderMoeContainer = ({
    sticky,
    headerColor,
    scrollThreshold = 60,
    children,
}: HeaderMenuContainerProps) => {
    const [isPageScrolled, setIsPageScrolled] = useState(false);
    const [scrollRatio, setScrollRatio] = useState(0);

    const throttledHandler = useMemo(
        () =>
            throttle(
                () => {
                    // for theming class name
                    setIsPageScrolled(window.scrollY > scrollThreshold);
                    // for sticky transparency
                    setScrollRatio(Math.min((window.scrollY / scrollThreshold) * 0.7, 1));
                },
                300
            ),
        [scrollThreshold]
    );

    // Added class is-scrolled, could be styled header in mediaspace theme
    useEffect(() => {
        window.addEventListener("scroll", throttledHandler);
        return () => {
            window.removeEventListener("scroll", throttledHandler);
            throttledHandler.cancel();
        };
    }, [throttledHandler]);

    // header menu scroll context value
    const contextValue = useMemo<HeaderMenuScrollContextType>(
        () => ({
            headerColor,
            scrollRatio,
        }),
        [headerColor, scrollRatio]
    );

    return (
        <HeaderMenuScrollContext.Provider value={contextValue}>
            <StyledHeaderMenu sticky={sticky} className={"kms-ds-header-menu"} role={"banner"}>
                <StyledNavbar className={"kms-ds-header-menu-navbar" + (isPageScrolled ? " is-scrolled" : "")}>
                    {children}
                </StyledNavbar>
            </StyledHeaderMenu>
        </HeaderMenuScrollContext.Provider>
    );
};
